import type { AppV2Props } from '@cogo/react-renderer-v2'
import { compileCogoDesignContract } from '@cogo/headless/cogo-design-compiler'
import { compileCogoRenderPack, encodeRenderPack } from '@cogo/runtime-v2'

function hasRenderableRoot(block: unknown): boolean {
  const record = block as
    | { ui?: { root?: unknown }; root?: unknown }
    | null
    | undefined
  return Boolean(record?.ui?.root ?? record?.root)
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function getRenderableRoot(block: unknown): unknown {
  const record = asRecord(block)
  return asRecord(asRecord(record?.ui)?.root) ?? asRecord(record?.root)
}

function isSourceProjectedElement(value: unknown): boolean {
  const element = asRecord(value)
  if (!element) return false
  const options = asRecord(element.options)
  if (
    options?.sourceDerived === true ||
    element.source !== undefined ||
    element.sourceDesign !== undefined
  ) {
    return true
  }
  const children = Array.isArray(element.children) ? element.children : []
  return children.some(isSourceProjectedElement)
}

function shouldBypassRenderPackCompile(block: unknown): boolean {
  const root = getRenderableRoot(block)
  return isSourceProjectedElement(root)
}

export function buildPreviewRenderPackBytes(
  blocks: Record<string, unknown>,
): AppV2Props['renderPackBytes'] | undefined {
  const renderPackBytes: NonNullable<AppV2Props['renderPackBytes']> = {}
  for (const [blockName, block] of Object.entries(blocks)) {
    if (!hasRenderableRoot(block)) continue
    // Source-projected applications carry source-truth inline style/layout
    // objects that must reach the renderer unchanged for visual parity. The
    // design compiler intentionally deletes converted author style objects to
    // enforce tokenized generated-app discipline, so source-derived blocks
    // bypass the preview RenderPack path and render from the original JSON.
    if (shouldBypassRenderPackCompile(block)) continue
    const compiledDesign = compileCogoDesignContract(
      block as Record<string, unknown>,
    )
    renderPackBytes[blockName] = encodeRenderPack(
      compileCogoRenderPack(
        compiledDesign.blockJson as Parameters<typeof compileCogoRenderPack>[0],
      ),
    )
  }
  return Object.keys(renderPackBytes).length > 0 ? renderPackBytes : undefined
}
